import React, { useState, useEffect } from "react";
import Select from 'react-select';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import windowManager from "../../../../services/windowManager";

const RunStatements = () => {
  const [account, setAccount] = useState('all');
  const [providers, setProviders] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [statementNote, setStatementNote] = useState("Payment Details:\nHira Dental Laboratory \nAccount Number: 29724546\nSort Code: 52-30-02");
  const [statementFooter, setStatementFooter] = useState('');
  const [dontPrintZeroBalance, setDontPrintZeroBalance] = useState(() => localStorage.getItem('dontPrintZeroBalance') === 'true' || false);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [lastPreview, setLastPreview] = useState(null);
  const [hasPreviews, setHasPreviews] = useState(false);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [previousStatements, setPreviousStatements] = useState([]);
  const [userLabId, setUserLabId] = useState(null);

  // Function to sort array of objects alphabetically by label
  const sortAlphabetically = (array) => {
    return [...array].sort((a, b) => {
      const labelA = a.label || '';
      const labelB = b.label || '';
      return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
    });
  };

  // Fetch previous statements for the selected date
  const fetchPreviousStatements = async (date) => {
    try {
      const response = await axios.get('https://hms.backend.hiragroupcloud.com/api/statements/previous', {
        params: { date }
      });
      if (response.data.success && response.data.statements) {
        setPreviousStatements(response.data.statements);
      } else {
        setPreviousStatements([]);
      }
    } catch (error) {
      console.error('Error fetching previous statements:', error);
      setPreviousStatements([]);
    }
  };

  // Fetch the latest preview statement
  const fetchLatestPreview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://hms.backend.hiragroupcloud.com/api/statements/preview/latest', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.data) {
        setLastPreview(response.data.data);
        setHasPreviews(true);
        
        // Optional: Auto-populate fields from the last preview
        const previewData = response.data.data;
        if (previewData.filters) {
          setDontPrintZeroBalance(previewData.filters.dontPrintZeroBalance || false);
          
          // If you want to auto-populate accounts and providers
          if (previewData.filters.accountIds && previewData.filters.accountIds.length > 0) {
            setAccount(previewData.filters.accountIds);
          }
        }
      } else {
        setHasPreviews(false);
        setLastPreview(null);
      }
    } catch (error) {
      console.error('Error fetching latest preview:', error);
      setHasPreviews(false);
      setLastPreview(null);
    }
  };

  useEffect(() => {
    // Get userLabId from localStorage
    const labId = localStorage.getItem('userLabId');
    setUserLabId(labId);

    const fetchAccounts = async () => {
      try {
        setIsLoading(true);
        
        // Check if userLabId exists
        if (!labId) {
          console.error('No userLabId found in localStorage');
          setAccounts([{ value: 'all', label: 'All Accounts' }]);
          setIsLoading(false);
          return;
        }

        // Use the new endpoint with userLabId
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`);
        
        // Check if response has data and the structure is correct
        const fetchedAccounts = response.data.data 
          ? response.data.data.map(account => ({
              value: account.accountId,
              label: account.account_name
            }))
          : response.data.map(account => ({
              value: account.accountId,
              label: account.account_name
            }));
        
        const sortedAccounts = sortAlphabetically(fetchedAccounts);
        setAccounts([{ value: 'all', label: 'All Accounts' }, ...sortedAccounts]);
      } catch (error) {
        console.error('Error fetching lab accounts:', error);
        setAccounts([{ value: 'all', label: 'All Accounts' }]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccounts();
    fetchLatestPreview(); // Fetch the latest preview
    fetchPreviousStatements(selectedDate);

    // Update current time every minute
    const interval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // When selectedDate changes, fetch previous statements for that date
  useEffect(() => {
    if (selectedDate) {
      fetchPreviousStatements(selectedDate);
    }
  }, [selectedDate]);

  // Add warning if userLabId is not found
  useEffect(() => {
    if (!userLabId && !isLoading) {
      console.warn('No userLabId found in localStorage. Only showing "All Accounts" option.');
    }
  }, [userLabId, isLoading]);

  const fetchProviders = async (selectedAccountIds) => {
    if (selectedAccountIds.length === 0 || selectedAccountIds.includes('all')) {
      return [];
    }

    try {
      setIsLoadingProviders(true);
      const allProviders = [];

      for (const accountId of selectedAccountIds) {
        try {
          const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/contacts/${accountId}`);
          
          if (response.data && response.data.data) {
            const accountProviders = response.data.data.map(contact => ({
              value: contact._id || contact.contactId,
              label: contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unnamed Provider',
              accountId: accountId
            }));
            allProviders.push(...accountProviders);
          }
        } catch (error) {
          console.error(`Error fetching providers for account ${accountId}:`, error);
        }
      }

      const sortedProviders = sortAlphabetically(allProviders);
      return sortedProviders;
    } catch (error) {
      console.error('Error fetching providers:', error);
      return [];
    } finally {
      setIsLoadingProviders(false);
    }
  };

 const handleAccountChange = async (selectedOption) => {
  const selectedValue = selectedOption ? selectedOption.value : 'all';
  setAccount(selectedValue);

  setProviders([]);

  if (!selectedValue || selectedValue === 'all') {
    return;
  }

  const fetchedProviders = await fetchProviders([selectedValue]);
  setProviders(fetchedProviders);
};

  const loadProviders = async (inputValue, callback) => {
   if (!account || account === 'all') {
      callback([]);
      return;
    }

    const fetchedProviders = await fetchProviders([account]);
    
    const filtered = inputValue 
      ? fetchedProviders.filter(provider => 
          provider.label.toLowerCase().includes(inputValue.toLowerCase())
        )
      : fetchedProviders;
    
    const sortedFiltered = sortAlphabetically(filtered);
    callback(sortedFiltered);
  };

  const handleProviderChange = (selectedOptions) => {
    const sortedSelectedOptions = sortAlphabetically(selectedOptions || []);
    setProviders(sortedSelectedOptions);
  };

  const handlePreviewStatements = () => {
    try {
      setIsLoading(true);

    let accountsToProcess = [];

if (account === 'all') {
  accountsToProcess = accounts
    .filter(opt => opt.value !== 'all')
    .map(opt => opt.value);
} else {
  accountsToProcess = [account];
}

      if (accountsToProcess.length === 0) {
        alert('Please select at least one account.');
        setIsLoading(false);
        return;
      }

      const selectedProviderIds = providers.map(provider => provider.value);

      const selectedDateObj = new Date(selectedDate);
      const currentTime = new Date();
      
      selectedDateObj.setHours(
        currentTime.getHours(),
        currentTime.getMinutes(),
        currentTime.getSeconds(),
        currentTime.getMilliseconds()
      );

      const lastRunTime = previousStatements.length > 0 
        ? new Date(Math.max(...previousStatements.map(s => new Date(s.runDate).getTime())))
        : null;

      const queryParams = new URLSearchParams({
        accountIds: accountsToProcess.join(','),
        ...(selectedProviderIds.length > 0 && { 
          providerIds: selectedProviderIds.join(',') 
        }),
        date: selectedDateObj.toISOString(),
        statementNote: encodeURIComponent(statementNote),
        statementFooter: encodeURIComponent(statementFooter),
        dontPrintEbilling: "true",
        dontPrintZeroBalance: dontPrintZeroBalance.toString(),
        autoSaveStatements: "false",
        ...(lastRunTime && { lastRunTime: lastRunTime.toISOString() })
      });

      const fullUrl = `/reprint-statement?${queryParams.toString()}`;
      windowManager.openStatement(fullUrl);

    } catch (error) {
      console.error('Error preparing statement preview:', error);
      alert('Failed to prepare statement preview. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Updated function to view the latest preview
  const handleViewLatestPreview = () => {
    if (!lastPreview) {
      alert('No preview statement available');
      return;
    }
    
    try {
      // Open the preview statement page with the preview ID
      const previewUrl = `/preview-statement/${lastPreview.previewId}`;
      windowManager.openStatement(previewUrl);
    } catch (error) {
      console.error('Error opening preview:', error);
      alert('Failed to open preview. Please try again.');
    }
  };

  // Function to view a specific preview by ID
  const handleViewPreviewById = (previewId) => {
    if (!previewId) return;
    
    try {
      const previewUrl = `/preview-statement/${previewId}`;
      windowManager.openStatement(previewUrl);
    } catch (error) {
      console.error('Error opening preview:', error);
      alert('Failed to open preview. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = days[date.getDay()];
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${dayName}, ${day} ${month} ${year}, ${displayHours}:${minutes} ${ampm}`;
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const getCurrentTime = () => {
    return currentDateTime.toTimeString().slice(0, 5);
  };

  const getPreviewDateForDisplay = (preview) => {
    if (!preview) return '';
    return preview.previewGeneratedAt || preview.runDate || '';
  };

  const getPreviewTimeForDisplay = (preview) => {
    const dateString = getPreviewDateForDisplay(preview);
    if (!dateString) return '';
    return formatTimeOnly(dateString);
  };

  // Only save the still-active setting
  useEffect(() => {
    localStorage.setItem('dontPrintZeroBalance', dontPrintZeroBalance);
  }, [dontPrintZeroBalance]);

  // Show warning if no userLabId
  if (!userLabId && !isLoading) {
    console.warn('No userLabId found. Only showing "All Accounts" option.');
  }

  return (
    <div style={{ padding: "2rem" }}>
      <h2 style={{ fontSize: "28px", fontWeight: "bold", marginBottom: "1.5rem", color: "#004aad", textAlign: "left" }}>
        Run Statements
      </h2>

      {/* Show warning if no userLabId */}
      {!userLabId && !isLoading && (
        <div style={{
          backgroundColor: "#fff3cd",
          border: "1px solid #ffc107",
          borderRadius: "4px",
          padding: "1rem",
          marginBottom: "1rem",
          color: "#856404"
        }}>
          <strong>Warning:</strong> No lab ID found. Please ensure you're logged in and your lab ID is stored in localStorage.
        </div>
      )}

      <div>
        <div style={{ borderBottom: "2px solid #004aad" }}>
          <strong style={{ color: "#004aad" }}>Statement Details</strong>
        </div>
        <div style={{ backgroundColor: "#e6e6e6", padding: "1rem" }}>
          {/* <p style={{ color: "#666" }}>
            {lastPreview 
              ? `Preview statements were last created at ${formatDate(getPreviewDateForDisplay(lastPreview))}` 
              : `No previous previews found`}
          </p>

          {hasPreviews && lastPreview && (
            <div style={{
              backgroundColor: "#ffffff",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "1rem",
              marginBottom: "1.5rem"
            }}>
              <h3 style={{ 
                margin: "0 0 0.5rem 0", 
                fontSize: "1.1rem", 
                fontWeight: "bold", 
                color: "#333" 
              }}>
                Statement Previews Available
              </h3>
              <p style={{ margin: "0.5rem 0", fontSize: "1rem" }}>
                There are statement previews available that were created at{' '}
                <span style={{ fontWeight: "bold", color: "#004aad" }}>
                  {formatDate(getPreviewDateForDisplay(lastPreview))}
                </span>.
                <a 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleViewLatestPreview();
                  }}
                  style={{
                    color: "#0066cc",
                    textDecoration: "underline",
                    marginLeft: "0.5rem",
                    cursor: "pointer"
                  }}
                >
                  View Them
                </a>
              </p>
              <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "0.5rem" }}>
                If you do not wish to use these previews, continue below to create new previews. Doing so will overwrite existing previews.
              </p>
            </div>
          )} */}

          {/* Account Dropdown */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>
              Account
            </label>
            <Select
              options={accounts}
              value={accounts.find(opt => opt.value === account)}
              onChange={handleAccountChange}
              placeholder={isLoading ? "Loading accounts..." : "Select Account..."}
              isDisabled={isLoading}
              styles={{ 
                control: (base) => ({ 
                  ...base, 
                  border: "1px solid #ccc", 
                  borderRadius: "4px",
                  backgroundColor: isLoading ? "#f5f5f5" : "#fff"
                }) 
              }}
            />

            {/* Selected count text below dropdown */}
            {account.length > 0 && account !== 'all' && (
              <div style={{ fontSize: "0.9rem", color: "#666", marginTop: "0.25rem" }}>
                1 account selected
              </div>
            )}
            {userLabId && accounts.length > 1 && (
              <div style={{ fontSize: "0.9rem", color: "#666", marginTop: "0.25rem" }}>
                Showing {accounts.length - 1} account(s)
              </div>
            )}
          </div>

          {/* Provider Dropdown */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>
              Providers (Contacts)
              {isLoadingProviders && (
                <span style={{ marginLeft: "0.5rem", fontSize: "0.9rem", color: "#666" }}>
                  Loading...
                </span>
              )}
            </label>
            <AsyncSelect
              isMulti
              cacheOptions
              defaultOptions
              loadOptions={loadProviders}
              value={providers}
              onChange={handleProviderChange}
              placeholder={account.length === 0 || account === 'all' 
                ? "Select accounts first to load providers" 
                : "Select Providers (Optional)"}
              isDisabled={isLoading || account.length === 0 || account === 'all' || isLoadingProviders}
              isLoading={isLoadingProviders}
              noOptionsMessage={() => {
                if (account.length === 0 || account === 'all') {
                  return "Please select specific accounts first";
                }
                return "No providers found or type to search";
              }}
              styles={{ 
                control: (base) => ({ 
                  ...base, 
                  border: "1px solid #ccc", 
                  borderRadius: "4px",
                  backgroundColor: "#fff"
                }) 
              }}
            />
            <div style={{ fontSize: "0.9rem", color: "#666", marginTop: "0.25rem" }}>
              {account.length > 0 && account !== 'all' && providers.length > 0 
                ? `${providers.length} provider(s) selected` 
                : "Select specific accounts to see available providers"}
            </div>
          </div>

          {/* Statement Date Selection */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>
              Statement Date<span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{ 
                width: "100%", 
                padding: "0.5rem", 
                border: "1px solid #ccc", 
                borderRadius: "4px",
                backgroundColor: "#fff"
              }}
              disabled={isLoading}
            />
            <div style={{ fontSize: "0.9rem", color: "#666", marginTop: "0.25rem" }}>
              Time will be automatically set to current time ({getCurrentTime()})
            </div>
          </div>

          {/* Previous Statements Warning */}
          {previousStatements.length > 0 && (
            <div style={{
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "4px",
              padding: "1rem",
              marginBottom: "1.5rem"
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                marginBottom: '0.5rem' 
              }}>
                <h3 style={{ 
                  margin: "0 0 0.5rem 0", 
                  fontSize: "1.1rem", 
                  fontWeight: "bold", 
                  color: "#856404" 
                }}>
                  ⚠️ Previous Statements Available
                </h3>
                <div style={{ fontSize: "0.9rem", color: "#666" }}>
                  {previousStatements.length} statement(s) on {new Date(selectedDate).toLocaleDateString()}
                </div>
              </div>
              
              <p style={{ margin: "0.5rem 0", fontSize: "1rem", color: "#856404" }}>
                Previous statements were created on this date. This run will include only invoices generated since the last statement.
              </p>
              
              <div style={{ marginTop: "0.5rem" }}>
                <strong style={{ fontSize: "0.9rem" }}>Previous runs:</strong>
                <div style={{ marginTop: "0.25rem" }}>
                  {previousStatements
                    .sort((a, b) => new Date(b.runDate) - new Date(a.runDate))
                    .map((statement, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.25rem 0',
                        borderBottom: index < previousStatements.length - 1 ? '1px solid #eee' : 'none'
                      }}>
                        <span style={{ fontSize: "0.9rem" }}>
                          {getPreviewTimeForDisplay(statement)} - {statement.accountCount || 1} account(s)
                        </span>
                        <button
                          onClick={() => handleViewPreviewById(statement.previewId)}
                          style={{
                            backgroundColor: "transparent",
                            border: "1px solid #856404",
                            color: "#856404",
                            padding: "0.25rem 0.75rem",
                            borderRadius: "4px",
                            fontSize: "0.8rem",
                            cursor: "pointer"
                          }}
                        >
                          View
                        </button>
                      </div>
                    ))}
                </div>
              </div>
              
              <p style={{ color: "#856404", fontSize: "0.9rem", marginTop: "0.5rem", fontStyle: "italic" }}>
                Creating new statements will include only invoices generated since {formatTimeOnly(
                  previousStatements.sort((a, b) => new Date(b.runDate) - new Date(a.runDate))[0]?.runDate
                ) || 'the last run'}.
              </p>
            </div>
          )}

          {/* Statement Note */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Statement Note</label>
            <textarea
              value={statementNote}
              onChange={(e) => setStatementNote(e.target.value)}
              style={{ 
                width: "100%", 
                padding: "0.5rem", 
                height: "100px", 
                border: "1px solid #ccc", 
                borderRadius: "4px",
                backgroundColor: "#fff"
              }}
              disabled={isLoading}
            />
          </div>

          {/* Statement Footer */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Statement Footer</label>
            <textarea
              value={statementFooter}
              onChange={(e) => setStatementFooter(e.target.value)}
              style={{ 
                width: "100%", 
                padding: "0.5rem", 
                height: "100px", 
                border: "1px solid #ccc", 
                borderRadius: "4px",
                backgroundColor: "#fff"
              }}
              disabled={isLoading}
            />
            <span style={{ fontSize: "0.9em", color: "#666", display: "block", marginTop: "0.25rem" }}>
              {1000 - statementFooter.length} characters left
            </span>
          </div>

          {/* Options */}
          <div style={{ marginBottom: "2rem" }}>
            <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", opacity: 0.6 }}>
              <input
                type="checkbox"
                id="dontPrintEbilling"
                checked={false}
                disabled
                style={{ marginRight: "0.5rem", cursor: "not-allowed" }}
              />
              <label 
                htmlFor="dontPrintEbilling" 
                style={{ fontSize: "1rem", fontWeight: "bold", color: "#777" }}
              >
                Don't Print Ebilling Statements 
              </label>
            </div>

            <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center" }}>
              <input
                type="checkbox"
                id="dontPrintZeroBalance"
                style={{ marginRight: "0.5rem" }}
                checked={dontPrintZeroBalance}
                onChange={() => setDontPrintZeroBalance(!dontPrintZeroBalance)}
              />
              <label htmlFor="dontPrintZeroBalance" style={{ fontSize: "1rem", fontWeight: "bold" }}>
                Don't Print Zero Balance Statements
              </label>
            </div>

            <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", opacity: 0.6 }}>
              <input
                type="checkbox"
                id="autoSaveStatements"
                checked={false}
                disabled
                style={{ marginRight: "0.5rem", cursor: "not-allowed" }}
              />
              <label 
                htmlFor="autoSaveStatements" 
                style={{ fontSize: "1rem", fontWeight: "bold", color: "#777" }}
              >
                Auto-save statements without preview
              </label>
            </div>
          </div>

          {/* Preview Button */}
          <button
            onClick={handlePreviewStatements}
            style={{ 
              backgroundColor: "#004aad", 
              color: "white", 
              padding: "0.75rem 1.5rem", 
              border: "none", 
              borderRadius: "4px",
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              fontSize: "1rem",
              fontWeight: "bold",
              minWidth: "200px"
            }}
            disabled={isLoading || !account || (!userLabId && account !== 'all')}
            title={!userLabId && account !== 'all' ? "Lab ID not found. Cannot load accounts." : ""}
          >
            {isLoading ? 'Processing...' : 'Preview Statements'}
          </button>
          
          {!userLabId && account !== 'all' && (
            <p style={{ color: "#ff0000", marginTop: "0.5rem", fontSize: "0.9rem" }}>
              Cannot load specific accounts: Lab ID not found. Please log in again.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RunStatements;